import { Badge } from "@nimit9/signet-ui/components/badge"
import { Button } from "@nimit9/signet-ui/components/button"
import { Checkbox } from "@nimit9/signet-ui/components/checkbox"
import { Dialog, DialogContent, DialogTitle } from "@nimit9/signet-ui/components/dialog"
import { Drawer, DrawerContent, DrawerTitle } from "@nimit9/signet-ui/components/drawer"
import { Label } from "@nimit9/signet-ui/components/label"
import { Skeleton } from "@nimit9/signet-ui/components/skeleton"
import { Textarea } from "@nimit9/signet-ui/components/textarea"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { EyeOffIcon, LockIcon, ScanFaceIcon } from "lucide-react"
import { createContext, useContext, useEffect, useState } from "react"
import toast from "react-hot-toast"
import { MnemonicDisplay } from "@/components/common/mnemonic-display"
import { PinInput, weakPinReason } from "@/components/common/pin-input"
import { useAuthCrypto, useSetRecoveryKey, useUpgradeKdf } from "@/hooks/use-auth-crypto"
import { requestEncryptionBackfill } from "@/hooks/use-backfill-encryption"
import { useHousehold } from "@/hooks/use-household"
import { useDataLocked, useVeiled } from "@/hooks/use-veiled"
import { api } from "@/lib/api"
import {
  checkVerifier,
  deriveKEK,
  deriveKey,
  generateRecoveryMnemonic,
  recoveryKeyFromMnemonic,
  unwrapDEK,
  unwrapKeyFromRecovery,
  wrapDekForNewPin,
  wrapKeyForRecovery,
} from "@/lib/crypto"
import {
  DEVICE_REMEMBER_DAYS,
  forgetDevice,
  rememberDevice,
  unlockWithDevice,
} from "@/lib/device-unlock"
import { claimKeyShare, readKeyShare } from "@/lib/household-key"
import { signOut } from "@/lib/sign-out"
import {
  forgetWebAuthn,
  hasWebAuthnEnrolment,
  isWebAuthnUnlockSupported,
  unlockWithWebAuthn,
} from "@/lib/webauthn-unlock"
import {
  AUTO_LOCK_FORGETS_DEVICE,
  INACTIVITY_LOCK_MINUTES,
  startInactivityWatcher,
  useCryptoStore,
} from "@/store/crypto"

/** The wrong-PIN copy (unlock.html). The recovery link sits in the same line. */
const WRONG_PIN_MESSAGE = "That PIN didn't match."

// No "N tries left": nothing counts attempts today. The PIN is checked
// entirely on this device (Argon2id KEK → AES-GCM unwrap of the DEK the
// server already sent), so the server never sees an attempt and a client-side
// counter would be fake security (clear site data → counter gone). A real
// limit needs a server-side design change; see the Phase 5 report.

/**
 * What to show when unlocking throws. A wrong PIN derives the wrong KEK, so
 * AES-GCM unwrap fails with an OperationError — which browsers raise with an
 * empty message, so showing `e.message` rendered no error at all.
 */
export function unlockErrorMessage(e: unknown): string {
  // Match on the name, not `instanceof DOMException`: the WebCrypto
  // implementation's DOMException class isn't always the page's global one.
  if (typeof e === "object" && e !== null && (e as { name?: unknown }).name === "OperationError")
    return WRONG_PIN_MESSAGE
  if (e instanceof Error && e.message) return e.message
  return "Unlock failed"
}

// ── The veil (private mode, locked) ──────────────────────────────────────────
//
// SAFETY DESIGN. While private mode is locked the app shell stays on screen —
// sidebar and tab bar visible but `inert` (see VeilInert, used in __root) —
// and the unlock sits on top as a bottom Drawer (phone) / centred Dialog
// (desktop). Behind it we render VeilBackdrop: a STATIC skeleton of a page,
// NOT the real route. The route (<Outlet/>) is not mounted at all until the
// key is in memory, so:
//   • no page data query runs while locked (nothing is fetched, so nothing
//     decrypted or ciphertext can be shown — hooks that decrypt never mount);
//   • plaintext amounts the API returns are never fetched, so the mask can't
//     be defeated by a rendering bug on some screen;
//   • the backdrop's only data is the household name (plaintext, already
//     cached by OnboardingGate).
// The server response is unchanged. The shell-level overlays that could
// fetch or render row data (command palette, detail panel, drop zone, quick
// add) are not mounted while veiled either (see __root).

/**
 * Mounts its children only while user data may load: not veiled AND the
 * encryption mode is known (overlays that can fetch or show rows).
 */
export function UnveiledOnly({ children }: { children: React.ReactNode }) {
  return useDataLocked() ? null : children
}

/** Makes the shell chrome (sidebar, tab bar) visible but non-interactive while veiled. */
export function VeilInert({ children }: { children: React.ReactNode }) {
  const veiled = useVeiled()
  if (!veiled) return <>{children}</>
  return (
    <div inert aria-hidden="true" data-testid="veil-inert" className="contents">
      {children}
    </div>
  )
}

/**
 * The page drawn behind the unlock: a header (household name, "Private" chip,
 * the eye-off mask icon) over skeleton cards shaped like Home. Static — no
 * queries, no numbers, no text from the vault.
 */
function VeilBackdrop({ title, isPrivate }: { title: string; isPrivate: boolean }) {
  return (
    <div
      data-testid="veil-backdrop"
      inert
      aria-hidden="true"
      className="flex min-h-0 flex-1 flex-col bg-bg-base"
    >
      <header className="flex h-[3.25rem] shrink-0 items-center justify-between gap-2 border-b border-border-default bg-bg-surface px-4 md:px-6">
        <p className="min-w-0 truncate text-callout font-medium text-text-primary">{title}</p>
        <div className="flex shrink-0 items-center gap-2">
          {isPrivate && (
            <Badge variant="secondary" className="gap-1">
              <LockIcon className="size-3" />
              Private
            </Badge>
          )}
          <span className="flex size-11 items-center justify-center text-text-muted md:size-8">
            <EyeOffIcon className="size-4" />
          </span>
        </div>
      </header>
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 p-4 md:p-6">
        <Skeleton className="h-40 ring-1 ring-border-subtle md:h-48" />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Skeleton className="h-28 ring-1 ring-border-subtle" />
          <Skeleton className="h-28 ring-1 ring-border-subtle" />
        </div>
        <Skeleton className="h-56 ring-1 ring-border-subtle md:h-72" />
      </div>
    </div>
  )
}

// Which container the gate content sits in, so its heading can be the
// Drawer/Dialog title (the sheet's accessible name).
const SheetKind = createContext<"drawer" | "dialog">("dialog")

/**
 * The unlock container: bottom sheet on phone, centred 420px dialog from md
 * up. Always open and not dismissible — Escape, backdrop taps and swipes are
 * ignored; the only ways out are unlocking or signing out.
 */
function UnlockSheet({ children }: { children: React.ReactNode }) {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const ignore = () => {}
  if (!isDesktop) {
    return (
      <Drawer open onOpenChange={ignore} disablePointerDismissal>
        <DrawerContent data-testid="unlock-sheet" data-sheet="drawer">
          <SheetKind.Provider value="drawer">
            <div className="overflow-y-auto px-4 pt-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
              {children}
            </div>
          </SheetKind.Provider>
        </DrawerContent>
      </Drawer>
    )
  }
  return (
    <Dialog open onOpenChange={ignore} disablePointerDismissal>
      <DialogContent
        showCloseButton={false}
        data-testid="unlock-sheet"
        data-sheet="dialog"
        className="max-h-[90dvh] overflow-y-auto p-6 sm:max-w-[420px]"
      >
        <SheetKind.Provider value="dialog">{children}</SheetKind.Provider>
      </DialogContent>
    </Dialog>
  )
}

/** The unlock.html stack: lock tile, then the state's own content. */
function GateCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-sm space-y-5">
      <div className="flex justify-center">
        <span className="flex size-13 items-center justify-center rounded-2xl bg-accent-green/10 text-accent-green">
          <LockIcon className="size-6" />
        </span>
      </div>
      {children}
    </div>
  )
}

function GateTitle({ title, children }: { title: string; children: React.ReactNode }) {
  const kind = useContext(SheetKind)
  const Title = kind === "drawer" ? DrawerTitle : DialogTitle
  return (
    <div className="space-y-1 text-center">
      <Title className="text-title font-semibold tracking-tight text-text-primary">{title}</Title>
      <p className="text-body text-text-muted">{children}</p>
    </div>
  )
}

function GateError({ children }: { children: React.ReactNode }) {
  return (
    <p role="alert" className="text-center text-caption font-medium text-destructive">
      {children}
    </p>
  )
}

// ── Recovery phrase display ──────────────────────────────────────────────────

function RecoveryDisplay({ mnemonic, onDone }: { mnemonic: string; onDone: () => void }) {
  const [confirmed, setConfirmed] = useState(false)

  return (
    <GateCard>
      <GateTitle title="Save your recovery phrase">
        Write these 12 words down and keep them safe. You will need them if you forget your PIN.
      </GateTitle>

      <MnemonicDisplay mnemonic={mnemonic} />

      <div className="flex items-center gap-2">
        <Checkbox
          id="recovery-saved"
          checked={confirmed}
          onCheckedChange={(v) => setConfirmed(v === true)}
        />
        <Label
          htmlFor="recovery-saved"
          className="cursor-pointer text-body font-normal text-text-secondary"
        >
          I have written down my recovery phrase
        </Label>
      </div>

      <Button size="touch" className="w-full" disabled={!confirmed} onClick={onDone}>
        I'm done, take me to the app
      </Button>
    </GateCard>
  )
}

// ── PIN Setup screen ─────────────────────────────────────────────────────────

function PinSetup({
  userId,
  onComplete,
  skipRecovery,
  existingDek,
}: {
  userId: string
  onComplete: () => void
  skipRecovery?: boolean
  /** Recovery flow: re-wrap this recovered DEK instead of minting a new one. */
  existingDek?: CryptoKey
}) {
  const [pin, setPin] = useState("")
  const [confirmPin, setConfirmPin] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [mnemonic, setMnemonic] = useState<string | null>(null)
  const [pendingKey, setPendingKey] = useState<CryptoKey | null>(null)

  const setRecoveryKey = useSetRecoveryKey()
  const upgradeKdf = useUpgradeKdf()
  const { setKey, setMode } = useCryptoStore()

  async function handleSetPin() {
    setError(null)
    if (pin.length !== 6) {
      setError("PIN must be 6 digits")
      return
    }
    if (pin !== confirmPin) {
      setError("PINs do not match")
      return
    }
    const weak = weakPinReason(pin)
    if (weak) {
      setError(weak)
      return
    }
    setLoading(true)
    try {
      // A new PIN invalidates any "remembered device" and Face ID record on
      // this browser.
      await forgetDevice(userId)
      await forgetWebAuthn(userId)
      // v2: use recovered DEK (recovery) or generate a random one (first setup),
      // derive KEK with Argon2id, wrap DEK
      const { dek, kdfSalt, wrappedDek } = await wrapDekForNewPin(pin, existingDek)

      // Persist v2 fields (no verifier needed — unwrap IS the check)
      await upgradeKdf.mutateAsync({ kdfVersion: 2, kdfSalt, wrappedDek })
      // First setup (no recovered DEK): sweep rows left over from standard mode.
      if (!existingDek) requestEncryptionBackfill()

      if (!skipRecovery) {
        const phrase = generateRecoveryMnemonic()
        const recoveryKey = await recoveryKeyFromMnemonic(phrase)
        const wrappedKey = await wrapKeyForRecovery(dek, recoveryKey)
        await setRecoveryKey.mutateAsync(wrappedKey)
        setPendingKey(dek)
        setMnemonic(phrase)
      } else {
        await setKey(dek)
        setMode("private")
        startInactivityWatcher()
        onComplete()
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to set PIN")
    } finally {
      setLoading(false)
    }
  }

  if (mnemonic && pendingKey) {
    return (
      <RecoveryDisplay
        mnemonic={mnemonic}
        onDone={async () => {
          await setKey(pendingKey)
          setMode("private")
          startInactivityWatcher()
          onComplete()
        }}
      />
    )
  }

  return (
    <GateCard>
      <GateTitle title="Set your Privacy PIN">
        Choose a 6-digit PIN. It encrypts your data locally — we can never see it.
      </GateTitle>

      <div className="space-y-4">
        <div className="space-y-2">
          <p className="text-center text-label uppercase tracking-wider text-text-muted">PIN</p>
          <PinInput value={pin} onChange={setPin} autoFocus aria-label="New 6-digit PIN" />
        </div>
        <div className="space-y-2">
          <p className="text-center text-label uppercase tracking-wider text-text-muted">
            Confirm PIN
          </p>
          <PinInput
            value={confirmPin}
            onChange={setConfirmPin}
            invalid={error === "PINs do not match"}
            aria-label="Confirm 6-digit PIN"
          />
        </div>
      </div>

      {error && <GateError>{error}</GateError>}

      <Button
        size="touch"
        className="w-full"
        onClick={handleSetPin}
        disabled={loading || pin.length !== 6 || confirmPin.length !== 6}
      >
        {loading ? "Setting up..." : "Set PIN & continue"}
      </Button>
    </GateCard>
  )
}

// ── Recovery entry screen ────────────────────────────────────────────────────

function RecoveryEntry({
  encryptedVerifier,
  encryptedRecoveryKey,
  userId,
  onRecovered,
  onBack,
}: {
  encryptedVerifier: string | null
  encryptedRecoveryKey: string
  userId: string
  onRecovered: () => void
  onBack: () => void
}) {
  const [phrase, setPhrase] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showSetPin, setShowSetPin] = useState(false)
  const [recoveredKey, setRecoveredKey] = useState<CryptoKey | null>(null)

  async function handleRecover() {
    setError(null)
    setLoading(true)
    try {
      const recoveryKey = await recoveryKeyFromMnemonic(phrase.trim())
      const userKey = await unwrapKeyFromRecovery(encryptedRecoveryKey, recoveryKey)
      // v1 had a verifier to check; v2 relies on unwrap succeeding.
      // For v1 users still in recovery flow, also verify against verifier if present.
      if (encryptedVerifier) {
        const valid = await checkVerifier(userKey, encryptedVerifier)
        if (!valid) {
          setError("Recovery phrase did not match. Please check your words.")
          return
        }
      }
      // Using the recovery phrase forgets this browser (remembered device AND
      // Face ID); the PIN it leads to also re-wraps the DEK, which invalidates
      // every other remembered / enrolled browser via the vault fingerprint.
      await forgetDevice(userId)
      await forgetWebAuthn(userId)
      setRecoveredKey(userKey)
      setShowSetPin(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Recovery failed")
    } finally {
      setLoading(false)
    }
  }

  if (showSetPin && recoveredKey) {
    return (
      <PinSetup userId={userId} skipRecovery existingDek={recoveredKey} onComplete={onRecovered} />
    )
  }

  return (
    <GateCard>
      <GateTitle title="Recover access">
        Paste your 12-word recovery phrase to regain access and set a new PIN.
      </GateTitle>

      <div className="space-y-1.5">
        <Textarea
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          placeholder="word1 word2 word3 … word12"
          rows={3}
          aria-label="Recovery phrase"
          aria-invalid={error ? true : undefined}
          className="resize-none text-body"
        />
        <p className="text-caption text-text-hint">
          Paste or type all 12 words separated by spaces, in order.
        </p>
      </div>

      {error && <GateError>{error}</GateError>}

      <div className="flex gap-2">
        <Button variant="ghost" size="touch" className="flex-1 text-text-muted" onClick={onBack}>
          Back
        </Button>
        <Button
          size="touch"
          className="flex-1"
          onClick={handleRecover}
          disabled={loading || !phrase.trim()}
        >
          {loading ? "Recovering..." : "Recover"}
        </Button>
      </div>
    </GateCard>
  )
}

// ── PIN Entry screen ─────────────────────────────────────────────────────────

// Consent copy for the opt-in. The idle clause follows the actual auto-lock
// behaviour and duration (store/crypto.ts), so it can't drift.
export const REMEMBER_DISCLOSURE = `Anyone using this browser can open Paisa without your PIN.${
  AUTO_LOCK_FORGETS_DEVICE
    ? ` Leaving a tab idle for ${INACTIVITY_LOCK_MINUTES} min forgets this device.`
    : ""
}`

function PinEntry({
  encryptedVerifier,
  encryptedRecoveryKey,
  userId,
  householdId,
  householdName,
  kdfVersion,
  kdfSalt,
  wrappedDek,
  onUnlocked,
}: {
  householdName?: string
  encryptedVerifier: string | null
  encryptedRecoveryKey: string | null
  userId: string
  householdId: string
  kdfVersion: number
  kdfSalt: string | null
  wrappedDek: string | null
  onUnlocked: () => void
}) {
  const [pin, setPin] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showRecovery, setShowRecovery] = useState(false)
  // Opt-in, unchecked by default. Only offered on the v2 path, where the
  // server's wrappedDek exists to scope the record to.
  const [rememberThisDevice, setRememberThisDevice] = useState(false)
  const canRemember = kdfVersion >= 2 && !!kdfSalt && !!wrappedDek && !!userId && !!householdId

  const { setKey, setMode } = useCryptoStore()

  // Face ID / Touch ID: offered only when the browser supports WebAuthn PRF
  // AND this browser holds a valid enrolment for this vault (v2 only).
  const [bioAvailable, setBioAvailable] = useState(false)
  const [bioBusy, setBioBusy] = useState(false)
  useEffect(() => {
    if (!canRemember || !wrappedDek) return
    let cancelled = false
    void (async () => {
      const ok =
        (await isWebAuthnUnlockSupported()) &&
        (await hasWebAuthnEnrolment({ userId, householdId, wrappedDek }))
      if (!cancelled) setBioAvailable(ok)
    })()
    return () => {
      cancelled = true
    }
  }, [canRemember, userId, householdId, wrappedDek])

  async function handleBiometric() {
    if (!wrappedDek) return
    setError(null)
    setBioBusy(true)
    const scope = { userId, householdId, wrappedDek }
    const res = await unlockWithWebAuthn(scope)
    setBioBusy(false)
    if (res.dek) {
      await setKey(res.dek)
      setMode("private")
      startInactivityWatcher()
      onUnlocked()
      return
    }
    // Fail closed to the PIN. A mismatch/failure has already deleted the record.
    if (res.reason !== "cancelled") setBioAvailable(await hasWebAuthnEnrolment(scope))
    setError(
      res.reason === "cancelled"
        ? "Face ID / Touch ID was cancelled. Enter your PIN."
        : "Couldn't unlock with Face ID / Touch ID. Enter your PIN.",
    )
  }

  async function handleUnlock() {
    setError(null)
    if (pin.length !== 6) return
    setLoading(true)
    try {
      if (kdfVersion >= 2 && kdfSalt && wrappedDek) {
        // v2: derive KEK with Argon2id, unwrap DEK (wrong PIN → throws)
        const kek = await deriveKEK(pin, kdfSalt)
        const dek = await unwrapDEK(wrappedDek, kek)
        // Remembering is best-effort and never blocks the unlock: on failure
        // rememberDevice deletes any partial record and returns false.
        if (rememberThisDevice && canRemember) {
          await rememberDevice(dek, { userId, householdId, wrappedDek })
        } else {
          await forgetDevice(userId)
        }
        await setKey(dek)
        setMode("private")
        startInactivityWatcher()
        onUnlocked()
      } else {
        // v1: PBKDF2 verify + migrate in-place to v2
        const oldKey = await deriveKey(userId, pin, userId)
        if (!encryptedVerifier) {
          setError("No verifier found — cannot verify PIN")
          setPin("")
          setLoading(false)
          return
        }
        const valid = await checkVerifier(oldKey, encryptedVerifier)
        if (!valid) {
          setError(WRONG_PIN_MESSAGE)
          setPin("")
          setLoading(false)
          return
        }
        // No in-place v1→v2 upgrade: wrapping the PBKDF2 key under Argon2id would still leave
        // every row brute-forceable from the PIN (#245 M2). Settings → Privacy offers the real
        // fix: turn Privacy mode off and on, which decrypts and re-keys under a random DEK.
        await setKey(oldKey)
        setMode("private")
        startInactivityWatcher()
        onUnlocked()
      }
    } catch (e) {
      setError(unlockErrorMessage(e))
      setPin("")
    } finally {
      setLoading(false)
    }
  }

  // Auto-submit when all 6 digits entered
  // biome-ignore lint/correctness/useExhaustiveDependencies: handleUnlock is stable within render cycle
  useEffect(() => {
    if (pin.length === 6) {
      handleUnlock()
    }
  }, [pin])

  if (showRecovery && encryptedRecoveryKey) {
    return (
      <RecoveryEntry
        // The verifier only means something on a v1 account; a stale one left beside a
        // wrapped DEK would reject a correct phrase (#245 M1).
        encryptedVerifier={wrappedDek ? null : encryptedVerifier}
        encryptedRecoveryKey={encryptedRecoveryKey}
        userId={userId}
        onRecovered={onUnlocked}
        onBack={() => setShowRecovery(false)}
      />
    )
  }

  return (
    <GateCard>
      <GateTitle title={householdName ? `Unlock ${householdName}` : "Unlock Paisa"}>
        Your numbers are encrypted on this device. Paisa&apos;s servers never see your PIN.
      </GateTitle>

      <PinInput value={pin} onChange={setPin} autoFocus invalid={error === WRONG_PIN_MESSAGE} />

      {bioAvailable && (
        <Button
          variant="outline"
          size="touch"
          className="w-full"
          disabled={bioBusy || loading}
          onClick={handleBiometric}
        >
          <ScanFaceIcon />
          {bioBusy ? "Waiting for Face ID / Touch ID…" : "Use Face ID / Touch ID instead"}
        </Button>
      )}

      {canRemember && (
        <div className="space-y-1">
          <div className="flex items-center justify-center gap-2">
            <Checkbox
              id="remember-device"
              aria-describedby="remember-device-disclosure"
              checked={rememberThisDevice}
              onCheckedChange={(v) => setRememberThisDevice(v === true)}
            />
            <Label
              htmlFor="remember-device"
              className="cursor-pointer text-body font-normal text-text-secondary"
            >
              Remember this device for {DEVICE_REMEMBER_DAYS} days
            </Label>
          </div>
          <p id="remember-device-disclosure" className="text-center text-caption text-text-muted">
            {REMEMBER_DISCLOSURE}
          </p>
        </div>
      )}

      {error && (
        <GateError>
          {error}
          {error === WRONG_PIN_MESSAGE && encryptedRecoveryKey && (
            <>
              {" "}
              <Button
                variant="link"
                className="h-auto p-0 text-caption font-medium"
                onClick={() => setShowRecovery(true)}
              >
                Use your recovery phrase
              </Button>
            </>
          )}
        </GateError>
      )}

      <Button
        size="touch"
        className="w-full"
        onClick={handleUnlock}
        disabled={loading || pin.length !== 6}
      >
        {loading ? "Unlocking..." : "Unlock"}
      </Button>

      {encryptedRecoveryKey && (
        <Button
          variant="link"
          className="h-auto w-full whitespace-normal text-body"
          onClick={() => setShowRecovery(true)}
        >
          Forgot the PIN? Use your 12-word recovery phrase
        </Button>
      )}
    </GateCard>
  )
}

// ── Profile query ─────────────────────────────────────────────────────────────

interface ProfileData {
  success: boolean
  data: { id: string; email: string; name: string | null; owner: string | null }
}

function useProfile() {
  return useQuery({
    queryKey: ["profile"],
    queryFn: () => api.get<ProfileData>("/profile"),
  })
}

// ── PinGate (main export) ────────────────────────────────────────────────────

// ── Household key claim (household private mode) ─────────────────────────────

function HouseholdKeyClaim({ userId }: { userId: string }) {
  const share = readKeyShare(window.location.hash)
  const qc = useQueryClient()
  const { setKey, setMode } = useCryptoStore()
  const [pin, setPin] = useState("")
  const [confirmPin, setConfirmPin] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [claimed, setClaimed] = useState<{ dek: CryptoKey; phrase: string } | null>(null)

  if (!share) {
    return (
      <GateCard>
        <GateTitle title="Household key needed">
          Your household uses Privacy mode. Ask the member who set it up to open Settings → Privacy
          → Share key, then open the link they send you on this device.
        </GateTitle>
        <Button variant="ghost" size="touch" className="w-full" onClick={() => signOut()}>
          Sign out
        </Button>
      </GateCard>
    )
  }

  if (claimed) {
    return (
      <RecoveryDisplay
        mnemonic={claimed.phrase}
        onDone={async () => {
          // Rows this member wrote in plaintext before claiming get encrypted on this unlock.
          requestEncryptionBackfill()
          await setKey(claimed.dek)
          setMode("private")
          startInactivityWatcher()
          window.history.replaceState(null, "", window.location.pathname + window.location.search)
          await qc.invalidateQueries({ queryKey: ["auth-crypto"] })
        }}
      />
    )
  }

  async function handleClaim() {
    if (!share) return
    setError(null)
    if (pin !== confirmPin) return setError("PINs do not match")
    const weak = weakPinReason(pin)
    if (weak) return setError(weak)
    setLoading(true)
    try {
      await forgetDevice(userId)
      await forgetWebAuthn(userId)
      setClaimed(await claimKeyShare(share, pin))
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't use this key link")
    } finally {
      setLoading(false)
    }
  }

  return (
    <GateCard>
      <GateTitle title="Join your household's Privacy mode">
        Choose your own 6-digit PIN. It unlocks the household's encrypted data on your devices.
      </GateTitle>
      <div className="space-y-4">
        <PinInput value={pin} onChange={setPin} autoFocus aria-label="New 6-digit PIN" />
        <PinInput value={confirmPin} onChange={setConfirmPin} aria-label="Confirm 6-digit PIN" />
      </div>
      {error && <GateError>{error}</GateError>}
      <Button
        size="touch"
        className="w-full"
        onClick={handleClaim}
        disabled={loading || pin.length !== 6 || confirmPin.length !== 6}
      >
        {loading ? "Unlocking…" : "Set PIN & unlock"}
      </Button>
    </GateCard>
  )
}

export function PinGate({ children }: { children: React.ReactNode }) {
  const { data: cryptoData, isLoading: cryptoLoading } = useAuthCrypto()
  const { data: profileData, isLoading: profileLoading } = useProfile()
  // Already cached by OnboardingGate, which only renders us once it exists.
  const { data: householdData } = useHousehold()
  const isUnlocked = useCryptoStore((s: { isUnlocked: boolean }) => s.isUnlocked)
  const { setKey, setMode } = useCryptoStore()
  // "pending" while we check for a remembered-device record, so the PIN
  // screen doesn't flash before a silent unlock.
  const [deviceCheck, setDeviceCheck] = useState<"pending" | "done">("pending")

  const isLoading = cryptoLoading || profileLoading
  const isPrivate = cryptoData?.encryptionMode === "private"
  const userId = profileData?.data?.id ?? ""
  const householdId = householdData?.household?.id ?? ""
  const serverWrappedDek = cryptoData?.wrappedDek ?? null
  const kdfV2 = (cryptoData?.kdfVersion ?? 1) >= 2 && !!cryptoData?.kdfSalt

  // Remembered-device unlock: runs whenever the gate is locked in private
  // mode (app load, or after an auto-lock when AUTO_LOCK_FORGETS_DEVICE is
  // false). Explicit Lock / logout / auto-lock delete the record before
  // clearing the key, so they land on the PIN prompt. Fails closed.
  useEffect(() => {
    if (isLoading || !isPrivate) return
    if (isUnlocked) {
      setDeviceCheck("pending")
      return
    }
    if (!kdfV2 || !serverWrappedDek || !userId || !householdId) {
      setDeviceCheck("done")
      return
    }
    let cancelled = false
    unlockWithDevice({ userId, householdId, wrappedDek: serverWrappedDek }).then(async (dek) => {
      if (cancelled) return
      if (dek) {
        await setKey(dek)
        setMode("private")
        startInactivityWatcher()
        toast("Unlocked on this device", { id: "device-unlock", duration: 2500 })
      } else {
        setDeviceCheck("done")
      }
    })
    return () => {
      cancelled = true
    }
  }, [
    isLoading,
    isPrivate,
    isUnlocked,
    kdfV2,
    serverWrappedDek,
    userId,
    householdId,
    setKey,
    setMode,
  ])

  const householdName = householdData?.household?.name ?? ""
  const backdropTitle = householdName || "Paisa"

  // Mode not known yet: a neutral backdrop, never the route — in private mode
  // the route would fetch rows it can't decrypt yet.
  if (isLoading) {
    return <VeilBackdrop title={backdropTitle} isPrivate={false} />
  }

  const encryptionMode = cryptoData?.encryptionMode ?? "standard"
  const encryptedVerifier = cryptoData?.encryptedVerifier ?? null
  const encryptedRecoveryKey = cryptoData?.encryptedRecoveryKey ?? null
  const kdfVersion = cryptoData?.kdfVersion ?? 1
  const kdfSalt = cryptoData?.kdfSalt ?? null
  const wrappedDek = cryptoData?.wrappedDek ?? null

  // Standard mode — transparent passthrough
  // Household private mode: another member holds the household key. Writing plaintext or minting
  // a second key would split the household's data, so fail closed until this member claims it.
  if (encryptionMode === "standard" && cryptoData?.householdPrivate) {
    return (
      <>
        <VeilBackdrop title={backdropTitle} isPrivate />
        <UnlockSheet>
          <HouseholdKeyClaim userId={userId} />
        </UnlockSheet>
      </>
    )
  }

  if (encryptionMode === "standard") {
    return <>{children}</>
  }

  // Private mode — already unlocked
  if (isUnlocked) {
    return <>{children}</>
  }

  // Private mode, locked, still checking for a remembered device: the veil
  // with no sheet, so a remembered device lifts it without the sheet flashing.
  if (deviceCheck === "pending") {
    return <VeilBackdrop title={backdropTitle} isPrivate />
  }

  return (
    <>
      <VeilBackdrop title={backdropTitle} isPrivate />
      <UnlockSheet>
        {!encryptedVerifier && !wrappedDek ? (
          // No verifier and no wrappedDek yet → first-time PIN setup
          <PinSetup userId={userId} onComplete={() => {}} />
        ) : (
          // PIN entry (v1 or v2)
          <PinEntry
            encryptedVerifier={encryptedVerifier}
            encryptedRecoveryKey={encryptedRecoveryKey}
            userId={userId}
            householdId={householdId}
            householdName={householdName}
            kdfVersion={kdfVersion}
            kdfSalt={kdfSalt}
            wrappedDek={wrappedDek}
            onUnlocked={() => {}}
          />
        )}
      </UnlockSheet>
    </>
  )
}
